<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;max-width:1300px;">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/students')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="st-page-title"><i class="mdi mdi-card-account-details me-2"></i>Student ID Cards</h4>
      </div>
      <div class="d-flex gap-2 flex-wrap align-items-end">
        <select class="st-input" v-model="classId" @change="loadStudents" style="width:180px;padding:7px 10px;">
          <option value="">Select Class</option>
          <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
        <button class="btn st-btn-primary btn-sm" @click="selectAll">{{ allSelected ? '☐ Deselect' : '☑ Select All' }}</button>
        <button class="btn btn-sm" style="background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.4);" @click="showBranding=true" title="Configure 'Accepted at' branding shown on the back of the card">
          <i class="mdi mdi-tune-variant me-1"></i>Card Branding
        </button>
        <button class="btn btn-sm" style="background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.4);" @click="openCardFees">💳 Manage Card Fees</button>
        <button class="btn st-btn-success btn-sm" @click="printCards" :disabled="selectedStudents.length===0" title="Print on A4 paper for cutting">
          🖨 A4 Sheet ({{ selectedStudents.length }})
        </button>
        <button class="btn btn-sm" style="background:linear-gradient(135deg,#7C3AED,#5B21B6);color:#fff;border:none;font-weight:600;" @click="exportPrinterPdf" :disabled="selectedStudents.length===0" title="Card-sized PDF (one face per page, front+back alternating) — feed straight to a PVC card printer">
          📄 Printer PDF ({{ selectedStudents.length }})
        </button>
      </div>
    </div>

    <!-- ============ CARD FEES MODAL ============ -->
    <div v-if="showCardFees" class="cf-overlay" @click.self="showCardFees = false">
      <div class="cf-modal">
        <div class="cf-title">
          <span>💳 Manage Card Fees</span>
          <button class="cf-close" @click="showCardFees = false">✕</button>
        </div>

        <div v-if="cardStats.total" class="cf-stats">
          <div class="cf-stat"><div class="cf-stat-num">{{ cardStats.total }}</div><div class="cf-stat-lbl">Active students</div></div>
          <div class="cf-stat" style="border-color:rgba(16,185,129,.4);"><div class="cf-stat-num" style="color:#10B981;">{{ cardStats.paid }}</div><div class="cf-stat-lbl">Card paid &amp; valid</div></div>
          <div class="cf-stat" style="border-color:rgba(245,158,11,.4);"><div class="cf-stat-num" style="color:#F59E0B;">{{ cardStats.unpaid }}</div><div class="cf-stat-lbl">Card unpaid</div></div>
          <div class="cf-stat" style="border-color:rgba(239,68,68,.4);"><div class="cf-stat-num" style="color:#EF4444;">{{ cardStats.expired }}</div><div class="cf-stat-lbl">Card expired</div></div>
          <div class="cf-stat" style="border-color:rgba(168,85,247,.4);"><div class="cf-stat-num" style="color:#A855F7;">KSh {{ Number(cardFeeAmount).toLocaleString() }}</div><div class="cf-stat-lbl">Card fee · {{ cardValidityMonths }}-month validity</div></div>
        </div>

        <div class="d-flex gap-2 flex-wrap align-items-center mb-2">
          <select class="cf-input" v-model="cardClassId" @change="loadCardStatus">
            <option value="">All classes</option>
            <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
          <select class="cf-input" v-model="cardStatusFilter" @change="loadCardStatus">
            <option value="">All</option>
            <option value="paid">Paid &amp; valid</option>
            <option value="unpaid">Unpaid</option>
            <option value="expired">Expired</option>
          </select>
          <button class="btn btn-sm cf-btn-primary" @click="loadCardStatus" :disabled="cardLoading">↻ Refresh</button>
          <span style="color:#8BA4C4;font-size:12px;margin-left:auto;">{{ cardStudents.length }} shown</span>
        </div>

        <div v-if="cardLoading" style="text-align:center;padding:30px;"><div class="spinner-border" style="color:#4FC3F7;"></div></div>
        <div v-else-if="!cardStudents.length" style="text-align:center;color:#8BA4C4;padding:30px;">No students match this filter.</div>
        <div v-else class="cf-table-wrap">
          <table class="cf-table">
            <thead>
              <tr><th>Name</th><th>Adm No</th><th>Class</th><th>Wallet</th><th>Card status</th><th>Action</th></tr>
            </thead>
            <tbody>
              <tr v-for="s in cardStudents" :key="s.id">
                <td style="font-weight:600;">{{ s.name }}</td>
                <td>{{ s.admission_no }}</td>
                <td>{{ s.class_name || '—' }}</td>
                <td :style="{color: Number(s.wallet_balance) >= cardFeeAmount ? '#10B981' : '#F59E0B'}">KSh {{ Number(s.wallet_balance).toLocaleString() }}</td>
                <td><span class="cf-pill" :class="cardPillClass(s)">{{ cardPillLabel(s) }}</span></td>
                <td>
                  <button v-if="needsCharging(s)" class="btn btn-sm cf-btn-warn" @click="chargeOne(s)" :disabled="chargingId === s.id || Number(s.wallet_balance) < cardFeeAmount" :title="Number(s.wallet_balance) < cardFeeAmount ? 'Wallet too low — parent must top up first' : 'Charge KSh ' + cardFeeAmount + ' for the card'">
                    <span v-if="chargingId === s.id" class="spinner-border spinner-border-sm"></span>
                    <span v-else>💳 Charge</span>
                  </button>
                  <span v-else style="color:#10B981;">✓</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex gap-2 mt-3 flex-wrap">
          <button class="btn cf-btn-warn" @click="chargeAllEligible" :disabled="bulkCharging || !eligibleCount">
            <span v-if="bulkCharging" class="spinner-border spinner-border-sm me-1"></span>💳 Charge {{ eligibleCount }} eligible student{{ eligibleCount === 1 ? '' : 's' }}
          </button>
          <span v-if="bulkResult" :style="{color: bulkResult.indexOf('✓') === 0 ? '#10B981' : '#EF4444'}">{{ bulkResult }}</span>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:8px;padding:8px 16px;margin-left:auto;" @click="showCardFees = false">Close</button>
        </div>
      </div>
    </div>

    <!-- ============ CARD BRANDING MODAL ============ -->
    <!-- Configures the optional "Accepted at <partner>" block on the back of the card.
         Saves to sessionStorage so it persists across page reloads for the same admin
         session. Cleared on logout. -->
    <div v-if="showBranding" class="cf-overlay" @click.self="showBranding=false">
      <div class="cf-modal" style="max-width:560px;">
        <div class="cf-title">
          <span><i class="mdi mdi-tune-variant me-2"></i>Card Branding</span>
          <button class="cf-close" @click="showBranding=false">✕</button>
        </div>
        <div style="color:#8BA4C4;font-size:12px;margin-bottom:14px;">
          Configure the "Accepted at" block on the back of the card. Useful when a dining outlet, partner shop, or sponsor is associated with the card. Leave blank to hide the block entirely.
        </div>

        <div class="row g-3 mb-3">
          <div class="col-12">
            <label class="cf-lbl">Partner name (e.g. AFROBLISS)</label>
            <input class="cf-input" style="width:100%;" v-model="brandingForm.acceptedAtName" placeholder="AFROBLISS" />
          </div>
          <div class="col-12">
            <label class="cf-lbl">Partner subtitle (e.g. RESTAURANT)</label>
            <input class="cf-input" style="width:100%;" v-model="brandingForm.acceptedAtSubtitle" placeholder="RESTAURANT" />
          </div>
          <div class="col-12">
            <label class="cf-lbl">Partner logo URL (optional)</label>
            <input class="cf-input" style="width:100%;" v-model="brandingForm.acceptedAtLogo" placeholder="https://..." />
            <small style="color:#8BA4C4;font-size:11px;">Square image, transparent background works best. Will be shown to the left of the partner name.</small>
          </div>
        </div>

        <!-- Mini live preview of the footer block -->
        <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;margin-bottom:14px;">
          <div style="color:#8BA4C4;font-size:11px;font-weight:700;letter-spacing:.4px;margin-bottom:8px;">PREVIEW</div>
          <div v-if="brandingForm.acceptedAtName" style="display:flex;align-items:center;gap:8px;">
            <img v-if="brandingForm.acceptedAtLogo" :src="brandingForm.acceptedAtLogo" style="width:32px;height:32px;object-fit:contain;" />
            <div style="display:flex;flex-direction:column;">
              <div style="color:rgba(255,255,255,.85);font-size:10px;font-style:italic;">Accepted at</div>
              <div :style="{color:tc.accent,fontSize:'18px',fontWeight:900,letterSpacing:'.5px',lineHeight:1}">{{ brandingForm.acceptedAtName }}</div>
              <div v-if="brandingForm.acceptedAtSubtitle" :style="{color:tc.accent,fontSize:'11px',fontWeight:700,letterSpacing:'3px',marginTop:'2px'}">{{ brandingForm.acceptedAtSubtitle }}</div>
            </div>
          </div>
          <div v-else style="color:#4A6A8A;font-size:12px;font-style:italic;">No "Accepted at" block — back of card will only show "Powered by Simi Trac".</div>
        </div>

        <div class="d-flex gap-2">
          <button class="btn cf-btn-primary flex-fill" @click="saveBranding">
            <i class="mdi mdi-check me-1"></i>Save
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:8px;padding:8px 18px;" @click="clearBranding" :disabled="!acceptedAtName">Clear</button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:8px;padding:8px 18px;" @click="showBranding=false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Card Theme -->
    <div class="theme-bar">
      <span style="color:#8BA4C4;font-size:12px;font-weight:600;margin-right:8px;">THEME:</span>
      <button v-for="t in themes" :key="t.key" class="theme-btn" :class="{active:theme===t.key}" @click="theme=t.key">
        <span class="theme-dot" :style="{background:'linear-gradient(135deg,'+t.bg1+','+t.bg2+')'}"></span> {{ t.name }}
      </button>
    </div>

    <div v-if="loading" class="st-spinner"><span class="spinner-border"></span></div>
    <div v-if="!classId && !loading" style="text-align:center;padding:50px;color:#4A6A8A;"><div style="font-size:50px;margin-bottom:10px;">🪪</div><p>Select a class to generate ID cards</p></div>

    <!-- Hidden-unpaid banner — printable cards only -->
    <div v-if="classId && !loading && hiddenCount > 0" style="background:rgba(245,158,11,.1);border:1px solid rgba(245,158,11,.4);border-radius:10px;padding:12px 16px;margin-bottom:14px;display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
      <span style="font-size:20px;">⚠️</span>
      <div style="flex:1;min-width:200px;">
        <div style="color:#F59E0B;font-weight:700;font-size:13px;">{{ hiddenCount }} student{{ hiddenCount === 1 ? '' : 's' }} hidden — card fee not paid</div>
        <div style="color:#8BA4C4;font-size:12px;">Only students with a paid &amp; valid card are printable here. Charge the card fee first to print their IDs.</div>
      </div>
      <button class="btn btn-sm" style="background:rgba(245,158,11,.2);color:#F59E0B;border:1px solid rgba(245,158,11,.5);font-weight:600;" @click="openCardFees">💳 Open Manage Card Fees</button>
    </div>

    <div v-if="classId && !loading && !printableStudents.length && students.length === 0" style="text-align:center;padding:40px;color:#4A6A8A;">
      <div style="font-size:40px;margin-bottom:8px;">🪪</div>
      <p>No students in this class yet.</p>
    </div>
    <div v-else-if="classId && !loading && !printableStudents.length && students.length > 0" style="text-align:center;padding:40px;color:#F59E0B;">
      <div style="font-size:40px;margin-bottom:8px;">🚫</div>
      <p style="font-weight:600;">No printable cards in this class</p>
      <p style="color:#8BA4C4;font-size:13px;">All {{ students.length }} student{{ students.length === 1 ? '' : 's' }} have unpaid cards. Use <strong>Manage Card Fees</strong> to charge them first.</p>
    </div>

    <!-- Preview Grid -->
    <div class="id-preview-grid">
      <div v-for="s in printableStudents" :key="s.id" class="id-pair" @click="toggleSelect(s)">
        <div class="id-check" :class="{checked: s._selected}">{{ s._selected ? '✅' : '' }}</div>

        <!-- ===== FRONT (credit-card style) ===== -->
        <div class="card-face card-front" :style="{background:'linear-gradient(135deg,'+tc.bg1+' 0%,'+tc.bg2+' 100%)',color:'#fff'}">
          <div class="cf-pattern"></div>
          <div class="cf-left">
            <div class="cf-logo">
              <img v-if="institutionLogo" :src="institutionLogo" />
              <span v-else>{{ institutionName.charAt(0) }}</span>
            </div>
            <div class="cf-photo-frame" :style="{borderColor:tc.accent}">
              <div class="cf-photo-img">
                <img v-if="s.photo_url" :src="s.photo_url" class="cf-photo" />
                <div v-else class="cf-photo-empty">{{ s.name?.charAt(0) }}{{ s.name?.split(' ')[1]?.charAt(0) || '' }}</div>
              </div>
              <div class="cf-photo-label" :style="{background:tc.accent,color:tc.bg1}">STUDENT</div>
            </div>
          </div>
          <div class="cf-right">
            <div class="cf-school" :style="{borderColor:tc.accent}">{{ institutionName }}</div>
            <div class="cf-subtitle" :style="{color:tc.accent}">SMART CARD</div>
            <div class="cf-spacer"></div>
            <div class="cf-cardno" :style="{color:tc.accent}">{{ cardNumber(s) }}</div>
            <div class="cf-valid"><span class="cf-vlbl">VALID THRU</span> <span class="cf-vval" :style="{color:tc.accent}">{{ validThru(s) }}</span></div>
            <div class="cf-spacer"></div>
            <div class="cf-name" :style="{borderColor:tc.accent}">{{ (s.name || '').toUpperCase() }}</div>
            <div class="cf-idno">ID {{ idNumber(s) }}</div>
          </div>
          <canvas :ref="'qr_'+s.id" width="80" height="80" class="cf-qr-hidden"></canvas>
        </div>

        <!-- ===== BACK ===== -->
        <div class="card-face card-back" :style="{background:'linear-gradient(135deg,'+tc.bg1+' 0%,'+tc.bg2+' 100%)',color:'#fff'}">
          <div class="cb-pattern"></div>
          <div class="cb-magstripe"></div>
          <div class="cb-body">
            <div class="cb-block">
              <div class="cb-heading" :style="{color:tc.accent}">TERMS</div>
              <div class="cb-text">This card remains the property of {{ institutionName }}.</div>
            </div>
            <div class="cb-divider" :style="{borderColor:tc.accent}"></div>
            <div class="cb-block">
              <div class="cb-heading" :style="{color:tc.accent}">RETURN TO</div>
              <div class="cb-return-grid">
                <div class="cb-col">
                  <div class="cb-text">If found, please return to:</div>
                  <div class="cb-text-strong">{{ institutionName }}</div>
                  <div class="cb-text">{{ institutionAddress || 'P.O. Box 000, Nairobi.' }}</div>
                </div>
                <div class="cb-col cb-col-divider">
                  <div class="cb-text">{{ institutionStreet || institutionCity || 'Nairobi.' }}</div>
                </div>
                <div class="cb-col cb-col-divider">
                  <div class="cb-text">Email: {{ institutionEmail || 'info@school.ac.ke' }}</div>
                  <div class="cb-text">Phone: {{ institutionPhone || '+254 700 000 000' }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="cb-divider-spark"></div>
          <div class="cb-footer">
            <div class="cb-accepted" v-if="acceptedAtName">
              <img v-if="acceptedAtLogo" :src="acceptedAtLogo" class="cb-accepted-icon" alt="" />
              <div class="cb-accepted-block">
                <div class="cb-accepted-lbl">Accepted at</div>
                <div class="cb-accepted-name" :style="{color:tc.accent}">{{ acceptedAtName }}</div>
                <div v-if="acceptedAtSubtitle" class="cb-accepted-sub" :style="{color:tc.accent}">{{ acceptedAtSubtitle }}</div>
              </div>
            </div>
            <div class="cb-powered-stack">
              <div class="cb-powered-row">
                <span class="cb-powered-text">Powered by</span>
                <img :src="simiLogo" class="cb-simi-logo" alt="Simi Trac" />
              </div>
              <div class="cb-product">A PRODUCT OF SIMI SYSTEMS LTD.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'StudentIdCards',
  components: { Navigation },
  data() { return {
    classId:'', classes:[], students:[], loading:false, theme:'deep-navy',
    themes: [
      // Deep Navy Blue — matches the reference card exactly
      // Background: #001135 → #011545 (dark navy gradient)
      // Pattern accent: #021D5F (royal navy, lighter for the halftone wave)
      // Gold (headings, AFROBLISS, photo border): #D6A02A / #C9952B
      // Magnetic stripe: #09090E (near black)
      // Bright cyan accents: #00D7F2 (logo glow, divider spark)
      // Bright electric blue: #0461CD (simi trac logo)
      { key:'deep-navy', name:'Deep Navy Blue', bg1:'#001135', bg2:'#011545', accent:'#D6A02A' },
      { key:'navy', name:'Executive Navy', bg1:'#0A1A3A', bg2:'#1A3A6A', accent:'#C9A84C' },
      { key:'royal', name:'Royal Blue', bg1:'#1A4B8C', bg2:'#2E86DE', accent:'#F5D042' },
      { key:'emerald', name:'Emerald', bg1:'#064E3B', bg2:'#10B981', accent:'#F5D042' },
      { key:'maroon', name:'Maroon', bg1:'#5B1A1A', bg2:'#B91C1C', accent:'#D4A843' },
      { key:'purple', name:'Royal Purple', bg1:'#2D1854', bg2:'#7C3AED', accent:'#F5D042' },
      { key:'black', name:'Jet Black', bg1:'#111111', bg2:'#333333', accent:'#C9A84C' },
      { key:'teal', name:'Teal Ocean', bg1:'#0D3B4A', bg2:'#0D9488', accent:'#F0C35E' },
      { key:'burgundy', name:'Burgundy', bg1:'#4A0E2C', bg2:'#9B1B5A', accent:'#E8C36A' },
      { key:'forest', name:'Forest Green', bg1:'#1A3A1A', bg2:'#2D6A2D', accent:'#D4A843' },
      { key:'charcoal', name:'Charcoal Silver', bg1:'#1F2937', bg2:'#4B5563', accent:'#E5E7EB' },
      { key:'orange', name:'Burnt Orange', bg1:'#7C2D12', bg2:'#EA580C', accent:'#FBBF24' },
      { key:'wine', name:'Wine Red', bg1:'#450A1A', bg2:'#881337', accent:'#F5D042' },
      { key:'sky', name:'Sky Blue', bg1:'#0C4A6E', bg2:'#0EA5E9', accent:'#FFFFFF' },
      { key:'olive', name:'Military Olive', bg1:'#2A2F1A', bg2:'#556B2F', accent:'#D4A843' },
      { key:'copper', name:'Copper Brown', bg1:'#3D1F0E', bg2:'#92400E', accent:'#F5D042' },
      { key:'slate', name:'Slate Gray', bg1:'#1E293B', bg2:'#475569', accent:'#38BDF8' },
    ],
    institutionName: sessionStorage.getItem('institutionName') || 'SCHOOL NAME',
    institutionLogo: sessionStorage.getItem('institutionLogo') || '',
    simiLogo: 'https://api.paysol.africa/simitrack/uploads/logos/simi_trac_logo.png?v=20260628',
    institutionMotto: sessionStorage.getItem('institutionMotto') || 'Excellence in Education',
    institutionAddress: sessionStorage.getItem('institutionAddress') || '',
    institutionStreet: sessionStorage.getItem('institutionStreet') || '',
    institutionCity: sessionStorage.getItem('institutionCity') || '',
    institutionPhone: sessionStorage.getItem('institutionPhone') || '',
    institutionEmail: sessionStorage.getItem('institutionEmail') || '',
    // Optional "Accepted at <partner>" block on the back of the card
    // (e.g. dining outlet that accepts the card for payment).
    acceptedAtName: sessionStorage.getItem('acceptedAtName') || '',
    acceptedAtSubtitle: sessionStorage.getItem('acceptedAtSubtitle') || '',
    acceptedAtLogo: sessionStorage.getItem('acceptedAtLogo') || '',
    principalSignature: sessionStorage.getItem('principalSignature') || '',
    officialStamp: sessionStorage.getItem('officialStamp') || '',
    currentYear: new Date().getFullYear(),
    // Card branding modal (configures "Accepted at" block on the back)
    showBranding: false,
    brandingForm: {
      acceptedAtName: sessionStorage.getItem('acceptedAtName') || '',
      acceptedAtSubtitle: sessionStorage.getItem('acceptedAtSubtitle') || '',
      acceptedAtLogo: sessionStorage.getItem('acceptedAtLogo') || '',
    },
    // Card fees modal
    showCardFees: false, cardLoading: false,
    cardClassId: '', cardStatusFilter: '',
    cardStudents: [], cardStats: {}, cardFeeAmount: 500, cardValidityMonths: 12,
    chargingId: null, bulkCharging: false, bulkResult: '',
  }; },
  computed: {
    // Only students whose card_fee is paid and not expired are printable.
    // Hidden ones must be charged first via Manage Card Fees.
    printableStudents() { return this.students.filter(s => this.cardActive(s)); },
    hiddenCount() { return this.students.length - this.printableStudents.length; },
    selectedStudents() { return this.printableStudents.filter(s=>s._selected); },
    allSelected() { return this.printableStudents.length>0 && this.printableStudents.every(s=>s._selected); },
    tc() { return this.themes.find(t=>t.key===this.theme) || this.themes[0]; },
    eligibleCount() {
      return this.cardStudents.filter(s => this.needsCharging(s) && Number(s.wallet_balance) >= this.cardFeeAmount).length;
    },
  },
  mounted() { this.loadClasses(); this.loadSchoolInfo(); },
  methods: {
    formatDob(d) { if(!d) return '—'; const p=d.split('-'); return p[2]+'/'+p[1]+'/'+p[0]; },
    // ===== Card branding =====
    saveBranding() {
      const f = this.brandingForm;
      sessionStorage.setItem('acceptedAtName', f.acceptedAtName || '');
      sessionStorage.setItem('acceptedAtSubtitle', f.acceptedAtSubtitle || '');
      sessionStorage.setItem('acceptedAtLogo', f.acceptedAtLogo || '');
      this.acceptedAtName = f.acceptedAtName || '';
      this.acceptedAtSubtitle = f.acceptedAtSubtitle || '';
      this.acceptedAtLogo = f.acceptedAtLogo || '';
      this.showBranding = false;
    },
    clearBranding() {
      this.brandingForm = { acceptedAtName: '', acceptedAtSubtitle: '', acceptedAtLogo: '' };
      sessionStorage.removeItem('acceptedAtName');
      sessionStorage.removeItem('acceptedAtSubtitle');
      sessionStorage.removeItem('acceptedAtLogo');
      this.acceptedAtName = '';
      this.acceptedAtSubtitle = '';
      this.acceptedAtLogo = '';
    },
    // ========== Card Fees ==========
    cardActive(s) {
      if (s.card_fee_paid != 1) return false;
      if (!s.card_expires_at) return true;
      return new Date(s.card_expires_at) > new Date();
    },
    needsCharging(s) { return !this.cardActive(s); },
    cardPillClass(s) {
      if (this.cardActive(s)) return 'cf-pill-ok';
      if (s.card_fee_paid == 1) return 'cf-pill-warn';
      return 'cf-pill-bad';
    },
    cardPillLabel(s) {
      if (this.cardActive(s)) return 'Paid · valid to ' + (s.card_expires_at || '—');
      if (s.card_fee_paid == 1) return 'Expired ' + (s.card_expires_at || '');
      return 'Unpaid';
    },
    async openCardFees() {
      this.showCardFees = true;
      this.cardClassId = this.classId || '';
      this.cardStatusFilter = 'unpaid';
      await this.loadCardStatus();
    },
    async loadCardStatus() {
      this.cardLoading = true;
      try {
        const r = await executeJson({ function: 'getStudentsCardStatus', classId: this.cardClassId, status: this.cardStatusFilter }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.cardStudents = d.students || [];
          this.cardStats = d.stats || {};
          this.cardFeeAmount = d.card_fee || 500;
          this.cardValidityMonths = d.validity_months || 12;
        }
      } catch (e) { /* silent */ }
      this.cardLoading = false;
    },
    async chargeOne(s) {
      this.chargingId = s.id;
      try {
        const r = await executeJson({ function: 'chargeCardFee', studentId: s.id }, simitrack);
        if (r.data.success) {
          alert('✓ ' + r.data.message);
          await this.loadCardStatus();
          if (this.classId) this.loadStudents();   // refresh print grid so newly-paid student becomes printable
        } else {
          alert('✗ ' + (r.data.message || 'Failed'));
        }
      } catch (e) { alert('Connection error'); }
      this.chargingId = null;
    },
    async chargeAllEligible() {
      const eligible = this.cardStudents.filter(s => this.needsCharging(s) && Number(s.wallet_balance) >= this.cardFeeAmount);
      if (!eligible.length) return;
      if (!confirm(`Charge KSh ${this.cardFeeAmount} card fee on ${eligible.length} eligible student(s)? Each is deducted from their wallet.`)) return;
      this.bulkCharging = true; this.bulkResult = '';
      let ok = 0, fail = 0;
      for (const s of eligible) {
        try {
          const r = await executeJson({ function: 'chargeCardFee', studentId: s.id }, simitrack);
          if (r.data.success) ok++; else fail++;
        } catch (e) { fail++; }
      }
      this.bulkResult = `✓ Charged ${ok} student(s)` + (fail ? `, ${fail} failed` : '') + '.';
      await this.loadCardStatus();
      if (ok > 0 && this.classId) this.loadStudents();   // refresh print grid
      this.bulkCharging = false;
      setTimeout(() => { this.bulkResult = ''; }, 6000);
    },
    // Smart-card layout helpers
    cardNumber(s) {
      // Deterministic 12-digit number formatted as 4-4-4
      // Group 1 = institution-derived prefix (or 6202 default — matches the example design)
      // Group 2 + 3 = zero-padded student.id
      const instId = parseInt(sessionStorage.getItem('institutionId') || '0', 10) || 0;
      const prefix = instId > 0 ? String(6200 + instId).padStart(4, '0').slice(-4) : '6202';
      const padded = String(s.id || 0).padStart(8, '0');
      return prefix + ' ' + padded.slice(0, 4) + ' ' + padded.slice(4, 8);
    },
    validThru(s) {
      // Use card_expires_at if billing has set it, else default to current month + 2 years
      let target;
      if (s.card_expires_at) {
        target = new Date(s.card_expires_at);
      } else {
        target = new Date();
        target.setFullYear(target.getFullYear() + 2);
      }
      if (isNaN(target.getTime())) return '—';
      const mm = String(target.getMonth() + 1).padStart(2, '0');
      const yy = String(target.getFullYear()).slice(-2);
      return mm + '/' + yy;
    },
    idNumber(s) {
      // Prefer admission_no if it's mostly numeric, else derive an 8-digit ID from student.id + institution
      const adm = String(s.admission_no || '').replace(/[^0-9]/g, '');
      if (adm.length >= 4) return adm;
      const instId = parseInt(sessionStorage.getItem('institutionId') || '0', 10) || 1;
      return String((instId * 1000000) + (s.id || 0)).padStart(8, '0');
    },
    async loadClasses() { try{const r=await executeJson({function:'getClasses'},simitrack);if(r.data.success)this.classes=r.data.data||[];}catch(e){} },
    async loadSchoolInfo() {
      try {
        const r=await executeJson({function:'getSettings'},simitrack);
        if(r.data.success&&r.data.data?.profile) {
          const p=r.data.data.profile;
          if(p.name) { this.institutionName=p.name; sessionStorage.setItem('institutionName',p.name); }
          if(p.logo) { this.institutionLogo=p.logo; sessionStorage.setItem('institutionLogo',p.logo); }
          if(p.motto) { this.institutionMotto=p.motto; sessionStorage.setItem('institutionMotto',p.motto); }
          if(p.address) { this.institutionAddress=p.address; sessionStorage.setItem('institutionAddress',p.address); }
          if(p.phone) { this.institutionPhone=p.phone; sessionStorage.setItem('institutionPhone',p.phone); }
          if(p.email) { this.institutionEmail=p.email; sessionStorage.setItem('institutionEmail',p.email); }
          if(p.principalSignature) { this.principalSignature=p.principalSignature; sessionStorage.setItem('principalSignature',p.principalSignature); }
          if(p.officialStamp) { this.officialStamp=p.officialStamp; sessionStorage.setItem('officialStamp',p.officialStamp); }
        }
      } catch(e){}
    },
    async loadStudents() {
      if(!this.classId){this.students=[];return;}
      this.loading=true;
      try{
        const r=await executeJson({function:'getStudents',classId:this.classId,limit:200},simitrack);
        if(r.data.success) { this.students=(r.data.data?.students||r.data.data||[]).map(s=>({...s,_selected:true})); this.$nextTick(()=>this.renderCodes()); }
      }catch(e){}
      this.loading=false;
    },
    toggleSelect(s){s._selected=!s._selected;},
    selectAll(){const v=!this.allSelected;this.printableStudents.forEach(s=>s._selected=v);},
    renderCodes() {
      this.printableStudents.forEach(s=>{
        const qc=this.$refs['qr_'+s.id]; if(qc&&qc[0]) this.drawQR(qc[0],s.admission_no||''+s.id,80);
      });
    },
    drawQR(canvas,text,size) {
      const ctx=canvas.getContext('2d'); ctx.fillStyle='#fff'; ctx.fillRect(0,0,size,size);
      ctx.fillStyle='#000'; const bs=Math.floor(size/25); const m=Math.floor((size-bs*21)/2);
      // Finder patterns
      [[0,0],[14,0],[0,14]].forEach(([r,c])=>{
        for(let i=0;i<7;i++)for(let j=0;j<7;j++){
          const fill=(i===0||i===6||j===0||j===6||(i>=2&&i<=4&&j>=2&&j<=4));
          if(fill){ctx.fillStyle='#000';ctx.fillRect(m+(c+j)*bs,m+(r+i)*bs,bs,bs);}
        }
      });
      // Timing
      for(let i=8;i<13;i++){if(i%2===0){ctx.fillStyle='#000';ctx.fillRect(m+6*bs,m+i*bs,bs,bs);ctx.fillRect(m+i*bs,m+6*bs,bs,bs);}}
      // Data from text
      let h=5381;for(let i=0;i<text.length;i++)h=((h<<5)+h)+text.charCodeAt(i);
      for(let r=0;r<21;r++)for(let c=0;c<21;c++){
        if(r<8&&c<8)continue;if(r<8&&c>12)continue;if(r>12&&c<8)continue;
        if(r===6||c===6)continue;
        const bit=((h>>>((r*21+c)%31))^(h>>>((r+c)%17)))&1;
        if(bit){ctx.fillStyle='#000';ctx.fillRect(m+c*bs,m+r*bs,bs,bs);}
      }
      // Admission number below
      ctx.fillStyle='#fff';ctx.fillRect(0,size-12,size,12);
      ctx.fillStyle='#000';ctx.font='bold 8px monospace';ctx.textAlign='center';
      ctx.fillText(text,size/2,size-3);
    },

    // ===== Shared print helpers =====
    // Both A4-sheet print + CR80 printer-feed PDF reuse the SAME card markup.
    // Keeping these in one place so a design tweak can't drift between the two outputs.
    _cardPrintCss(tc) {
      return `
.card{width:85.6mm;height:54mm;border-radius:3mm;overflow:hidden;position:relative;font-size:7pt;display:flex;flex-direction:column;}

/* FRONT */
.front{background:linear-gradient(135deg,${tc.bg1} 0%,${tc.bg2} 100%);color:#F2F4F8;flex-direction:row;padding:3mm 4mm;position:relative;}
.f-pattern{position:absolute;top:0;right:0;bottom:0;width:65%;
  background-image:radial-gradient(circle, rgba(255,255,255,.22) .22mm, transparent .32mm);
  background-size:1.8mm 1.8mm;
  -webkit-mask-image:radial-gradient(ellipse 80% 100% at 110% 50%, #000 30%, transparent 80%);
  mask-image:radial-gradient(ellipse 80% 100% at 110% 50%, #000 30%, transparent 80%);}
.f-left{width:23mm;display:flex;flex-direction:column;align-items:center;flex-shrink:0;position:relative;z-index:1;}
.f-logo{width:15mm;height:15mm;border-radius:50%;display:flex;align-items:center;justify-content:center;overflow:hidden;background:transparent;color:#F2F4F8;font-weight:900;font-size:16pt;margin-bottom:1mm;}
.f-logo img{width:100%;height:100%;object-fit:contain;}
.f-tagline{font-size:5pt;color:${tc.accent};font-weight:600;text-align:center;margin-top:.4mm;margin-bottom:1mm;line-height:1.1;font-style:italic;letter-spacing:.2px;max-width:22mm;}
.f-photo-frame{width:21mm;border:.6mm solid ${tc.accent};border-radius:1mm;overflow:hidden;display:flex;flex-direction:column;}
.f-photo-img{height:25mm;background:#fff;position:relative;overflow:hidden;}
.f-photo-img img{width:100%;height:100%;object-fit:cover;display:block;}
.f-photo-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#fff;color:${tc.bg1};font-size:13pt;font-weight:800;}
.f-photo-label{height:3.5mm;background:${tc.accent};color:${tc.bg1};font-size:6.5pt;font-weight:800;letter-spacing:.6px;text-align:center;line-height:3.5mm;}

.f-right{flex:1;display:flex;flex-direction:column;padding-left:4mm;position:relative;z-index:1;}
.f-school{font-size:8pt;font-weight:800;letter-spacing:.15px;line-height:1.15;text-transform:uppercase;border-bottom:.5mm solid ${tc.accent};padding-bottom:.8mm;margin-bottom:1mm;color:#F2F4F8;white-space:nowrap;overflow:hidden;font-family:'Montserrat',sans-serif;}
.f-subtitle{font-size:8pt;font-weight:600;letter-spacing:1.5px;color:${tc.accent};margin-bottom:0;font-family:'Montserrat',sans-serif;}
.f-cardno{font-size:13pt;font-weight:700;letter-spacing:.5mm;font-family:'OCR B','OCR-B','OCRB','Eurostile Extended','Eurostile','Roboto Mono','Share Tech Mono','Courier New',monospace;color:${tc.accent};line-height:1;margin-bottom:1.5mm;white-space:nowrap;}
.f-valid{font-size:6pt;color:rgba(255,255,255,.85);font-weight:600;letter-spacing:.8px;font-family:'Montserrat',sans-serif;}
.f-vlbl{margin-right:2mm;}
.f-vval{font-size:7.5pt;font-weight:700;color:${tc.accent};font-family:'OCR B','OCR-B','OCRB','Eurostile Extended','Eurostile','Roboto Mono','Share Tech Mono','Courier New',monospace;}
.f-spacer{flex:1;min-height:1mm;}
.f-name{font-size:10pt;font-weight:800;letter-spacing:.5px;border-bottom:.5mm solid ${tc.accent};padding-bottom:.5mm;margin-bottom:.6mm;color:#F2F4F8;line-height:1.1;text-transform:uppercase;font-family:'Montserrat',sans-serif;}
.f-idno{font-size:6.5pt;color:rgba(255,255,255,.85);letter-spacing:.4px;font-weight:500;font-family:'Montserrat',sans-serif;}

/* BACK */
.back{background:linear-gradient(135deg,${tc.bg1} 0%,${tc.bg2} 100%);color:#F2F4F8;display:flex;flex-direction:column;position:relative;}
.b-pattern{position:absolute;inset:0;background-image:radial-gradient(circle, rgba(255,255,255,.07) .22mm, transparent .32mm);background-size:2mm 2mm;pointer-events:none;}
.b-magstripe{height:7mm;background:#09090E;margin-top:3.5mm;width:100%;flex-shrink:0;position:relative;z-index:1;}
.b-body{padding:2.5mm 5mm 1mm;flex:1;display:flex;flex-direction:column;position:relative;z-index:1;}
.b-block{margin-bottom:.8mm;}
.b-heading{font-size:8pt;font-weight:800;letter-spacing:.6px;margin-bottom:.6mm;text-transform:uppercase;color:${tc.accent};}
.b-text{font-size:5.5pt;color:rgba(255,255,255,.92);line-height:1.4;}
.b-text-strong{font-size:6pt;color:#fff;font-weight:700;line-height:1.3;}
.b-divider{border-bottom:.2mm solid ${tc.accent};opacity:.45;margin:.8mm 0 1.2mm;}
.b-divider-spark{position:relative;border-bottom:.15mm solid rgba(0,215,242,.25);margin:.8mm 5mm 1.2mm;flex-shrink:0;z-index:1;}
.b-divider-spark::after{content:'';position:absolute;top:50%;left:50%;width:1.4mm;height:1.4mm;background:#00D7F2;border-radius:50%;transform:translate(-50%,-50%);box-shadow:0 0 1.5mm rgba(0,215,242,.85);}
.b-return-grid{display:grid;grid-template-columns:1.1fr 1fr 1.2fr;gap:2mm;margin-top:.4mm;}
.b-col-divider{border-left:.2mm solid rgba(255,255,255,.25);padding-left:2mm;}

.b-footer{display:flex;align-items:flex-end;justify-content:space-between;padding:0 5mm 2mm;position:relative;z-index:1;gap:3mm;}
.b-accepted{display:flex;align-items:center;gap:1.5mm;flex:1;min-width:0;}
.b-accepted-icon{width:8mm;height:8mm;object-fit:contain;flex-shrink:0;}
.b-accepted-block{display:flex;flex-direction:column;line-height:1.05;min-width:0;}
.b-accepted-lbl{font-size:5pt;color:rgba(255,255,255,.85);font-weight:500;font-style:italic;letter-spacing:.15px;margin-bottom:.2mm;}
.b-accepted-name{font-size:10pt;font-weight:900;letter-spacing:.25mm;line-height:1;text-transform:uppercase;color:${tc.accent};}
.b-accepted-sub{font-size:5.5pt;font-weight:700;letter-spacing:1.2mm;line-height:1.1;text-transform:uppercase;margin-top:.3mm;color:${tc.accent};}
.b-powered-stack{display:flex;flex-direction:column;align-items:flex-end;gap:.5mm;}
.b-powered-row{display:flex;align-items:center;gap:1.2mm;}
.b-powered-text{font-size:5.5pt;color:rgba(255,255,255,.78);font-weight:500;font-style:italic;}
.b-simi-logo{height:5.5mm;width:auto;object-fit:contain;}
.b-product{font-size:4.5pt;color:rgba(255,255,255,.55);letter-spacing:.3px;font-weight:600;text-align:right;}
      `;
    },
    _cardFrontPrintHtml(s) {
      const inst = this.institutionName;
      const initial = inst.charAt(0);
      const logoHtml = this.institutionLogo
        ? `<img src="${this.institutionLogo}" />`
        : initial;
      const photoHtml = s.photo_url
        ? `<img src="${s.photo_url}" />`
        : `<div class="f-photo-empty">${(s.name || '').charAt(0)}</div>`;
      return `<div class="card front">
        <div class="f-pattern"></div>
        <div class="f-left">
          <div class="f-logo">${logoHtml}</div>
          <div class="f-photo-frame">
            <div class="f-photo-img">${photoHtml}</div>
            <div class="f-photo-label">STUDENT</div>
          </div>
        </div>
        <div class="f-right">
          <div class="f-school">${inst}</div>
          <div class="f-subtitle">SMART CARD</div>
          <div class="f-spacer"></div>
          <div class="f-cardno">${this.cardNumber(s)}</div>
          <div class="f-valid"><span class="f-vlbl">VALID THRU</span> <span class="f-vval">${this.validThru(s)}</span></div>
          <div class="f-spacer"></div>
          <div class="f-name">${(s.name || '').toUpperCase()}</div>
          <div class="f-idno">ID ${this.idNumber(s)}</div>
        </div>
      </div>`;
    },
    _cardBackPrintHtml() {
      const inst = this.institutionName;
      const addr = this.institutionAddress || 'P.O. Box 000, Nairobi.';
      const street = this.institutionStreet || this.institutionCity || 'Nairobi.';
      const phone = this.institutionPhone || '+254 700 000 000';
      const email = this.institutionEmail || 'info@school.ac.ke';
      return `<div class="card back">
        <div class="b-pattern"></div>
        <div class="b-magstripe"></div>
        <div class="b-body">
          <div class="b-block">
            <div class="b-heading">TERMS</div>
            <div class="b-text">This card remains the property of ${inst}.</div>
          </div>
          <div class="b-divider"></div>
          <div class="b-block">
            <div class="b-heading">RETURN TO</div>
            <div class="b-return-grid">
              <div>
                <div class="b-text">If found, please return to:</div>
                <div class="b-text-strong">${inst}</div>
                <div class="b-text">${addr}</div>
              </div>
              <div class="b-col-divider"><div class="b-text">${street}</div></div>
              <div class="b-col-divider">
                <div class="b-text">Email: ${email}</div>
                <div class="b-text">Phone: ${phone}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="b-divider-spark"></div>
        <div class="b-footer">
          ${this.acceptedAtName ? `
          <div class="b-accepted">
            ${this.acceptedAtLogo ? `<img src="${this.acceptedAtLogo}" class="b-accepted-icon" />` : ''}
            <div class="b-accepted-block">
              <div class="b-accepted-lbl">Accepted at</div>
              <div class="b-accepted-name">${this.acceptedAtName}</div>
              ${this.acceptedAtSubtitle ? `<div class="b-accepted-sub">${this.acceptedAtSubtitle}</div>` : ''}
            </div>
          </div>
          ` : ''}
          <div class="b-powered-stack">
            <div class="b-powered-row">
              <span class="b-powered-text">Powered by</span>
              <img src="${this.simiLogo}" class="b-simi-logo" />
            </div>
            <div class="b-product">A PRODUCT OF SIMI SYSTEMS LTD.</div>
          </div>
        </div>
      </div>`;
    },

    printCards() {
      const tc = this.tc;
      const cards = this.selectedStudents;
      const inst = this.institutionName;

      const cardCss = this._cardPrintCss(tc);
      let html = `<!DOCTYPE html><html><head><title>${inst} — ID Cards</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700;800;900&display=swap" rel="stylesheet">
<style>
@page{size:A4 landscape;margin:6mm;}
*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important;}
body{font-family:'Montserrat','Segoe UI',Helvetica,Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
.sheet{display:flex;flex-wrap:wrap;gap:4mm;justify-content:center;align-content:flex-start;}
.pair{display:flex;gap:2mm;page-break-inside:avoid;margin-bottom:4mm;}
${cardCss}
</style></head><body><div class="sheet">`;

      cards.forEach(s => {
        html += `<div class="pair">${this._cardFrontPrintHtml(s)}${this._cardBackPrintHtml(s)}</div>`;
      });

      html += `</div></body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html); w.document.close();
      // Allow Google Fonts (Montserrat) + images to load before the print dialog
      setTimeout(() => w.print(), 1800);
    },

    // ===== Printer-feed PDF =====
    // Page size = CR80 card (85.6mm × 54mm landscape). Each card face is its own
    // page — front, back, front, back, ... — so the operator can save as PDF and
    // feed it straight to a PVC card printer's auto-duplex queue. Full bleed,
    // no margins, no cropmarks. Print dialog opens with "Save as PDF" preselected.
    exportPrinterPdf() {
      const tc = this.tc;
      const cards = this.selectedStudents;
      const inst = this.institutionName;

      const cardCss = this._cardPrintCss(tc);
      let html = `<!DOCTYPE html><html><head><title>${inst} — ID Cards (Printer PDF)</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700;800;900&display=swap" rel="stylesheet">
<style>
@page{size:85.6mm 54mm;margin:0;}
*{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important;}
html,body{width:85.6mm;height:54mm;}
body{font-family:'Montserrat','Segoe UI',Helvetica,Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;}
${cardCss}
.card{page-break-after:always;page-break-inside:avoid;border-radius:0;}
.card:last-child{page-break-after:auto;}
</style></head><body>`;

      cards.forEach(s => {
        html += this._cardFrontPrintHtml(s);
        html += this._cardBackPrintHtml(s);
      });

      html += `</body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html); w.document.close();
      setTimeout(() => {
        try { w.document.title = `${inst.replace(/\s+/g,'_')}_cards_printer.pdf`; } catch(e) { /* cosmetic */ }
        w.print();
      }, 1800);
    },
  }
};
</script>

<style scoped>
/* Montserrat for institution name + general card text. Card number uses an
   OCR-B/Eurostile-Extended stack with safe monospace fallbacks. */
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700;800;900&display=swap');

.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-input{background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-success{background:linear-gradient(135deg,#10B981,#059669);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}

.theme-bar{display:flex;align-items:center;gap:6px;margin-bottom:16px;flex-wrap:wrap;}
.theme-btn{display:flex;align-items:center;gap:6px;background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;padding:6px 12px;border-radius:8px;font-size:11px;font-weight:600;cursor:pointer;transition:.2s;}.theme-btn.active{border-color:#4FC3F7;color:#E8EDF4;background:#1A3A6A;}.theme-dot{width:14px;height:14px;border-radius:50%;display:block;}

.id-preview-grid{display:flex;flex-wrap:wrap;gap:16px;justify-content:center;}
.id-pair{display:flex;gap:8px;position:relative;cursor:pointer;transition:.2s;}.id-pair:hover{transform:translateY(-2px);}
.id-check{position:absolute;top:-6px;right:-6px;z-index:2;width:24px;height:24px;background:#0A1628;border:1px solid #1A3A6A;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;}.id-check.checked{background:rgba(16,185,129,.2);border-color:#10B981;}

/* Preview cards — credit-card style (CR80 ratio: 85.6mm × 54mm) */
.card-face{width:340px;height:215px;border-radius:14px;overflow:hidden;font-size:10px;display:flex;flex-direction:column;box-shadow:0 8px 24px rgba(0,0,0,.4);position:relative;}
.card-front{color:#fff;flex-direction:row;padding:14px 16px;font-family:'Montserrat','Segoe UI',Helvetica,Arial,sans-serif;}
.card-back{color:#fff;flex-direction:column;font-family:'Montserrat','Segoe UI',Helvetica,Arial,sans-serif;}

/* ===== FRONT ===== */
/* Right-side curved halftone sweep (like the reference's wave pattern) */
.cf-pattern{position:absolute;top:0;right:0;bottom:0;width:65%;
  background-image:radial-gradient(circle, rgba(255,255,255,.22) 1.1px, transparent 1.4px);
  background-size:7px 7px;
  -webkit-mask-image:radial-gradient(ellipse 80% 100% at 110% 50%, #000 30%, transparent 80%);
  mask-image:radial-gradient(ellipse 80% 100% at 110% 50%, #000 30%, transparent 80%);
  pointer-events:none;}

.cf-left{width:90px;display:flex;flex-direction:column;align-items:center;flex-shrink:0;position:relative;z-index:1;}
.cf-logo{width:58px;height:58px;border-radius:50%;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;color:#fff;font-weight:900;font-size:22px;background:transparent;margin-bottom:4px;}
.cf-logo img{width:100%;height:100%;object-fit:contain;}
.cf-tagline{font-size:6.5px;font-weight:600;text-align:center;letter-spacing:.3px;margin-top:2px;margin-bottom:5px;line-height:1.15;font-style:italic;max-width:84px;}

/* Photo + STUDENT label as a single gold-bordered unit */
.cf-photo-frame{width:80px;border:2.5px solid;border-radius:4px;overflow:hidden;display:flex;flex-direction:column;background:transparent;}
.cf-photo-img{height:96px;background:#fff;position:relative;overflow:hidden;}
.cf-photo{width:100%;height:100%;object-fit:cover;display:block;}
.cf-photo-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:800;background:#fff;color:#0A1A3A;}
.cf-photo-label{height:14px;font-size:9px;font-weight:800;letter-spacing:1.5px;text-align:center;line-height:14px;}

.cf-right{flex:1;display:flex;flex-direction:column;padding-left:14px;position:relative;z-index:1;}
.cf-school{font-size:11px;font-weight:800;letter-spacing:.3px;line-height:1.15;text-transform:uppercase;border-bottom:2px solid;padding-bottom:4px;margin-bottom:4px;white-space:nowrap;overflow:hidden;font-family:'Montserrat',sans-serif;}
.cf-subtitle{font-size:11px;font-weight:600;letter-spacing:2.5px;margin-bottom:0;font-family:'Montserrat',sans-serif;}
.cf-cardno{font-size:18px;font-weight:700;letter-spacing:1.6px;font-family:'OCR B','OCR-B','OCRB','Eurostile Extended','Eurostile','Roboto Mono','Share Tech Mono','Courier New',monospace;line-height:1;margin-bottom:7px;white-space:nowrap;}
.cf-valid{font-size:8px;color:rgba(255,255,255,.85);font-weight:600;letter-spacing:1px;font-family:'Montserrat',sans-serif;}
.cf-vlbl{margin-right:8px;}
.cf-vval{font-size:11px;font-weight:700;font-family:'OCR B','OCR-B','OCRB','Eurostile Extended','Eurostile','Roboto Mono','Share Tech Mono','Courier New',monospace;}
.cf-spacer{flex:1;min-height:6px;}
.cf-name{font-size:14px;font-weight:800;letter-spacing:.6px;border-bottom:2px solid;padding-bottom:3px;margin-bottom:3px;line-height:1.1;text-transform:uppercase;font-family:'Montserrat',sans-serif;}
.cf-idno{font-size:9px;color:rgba(255,255,255,.85);letter-spacing:.5px;font-weight:500;font-family:'Montserrat',sans-serif;}

.cf-qr-hidden{display:none;}

/* ===== BACK ===== */
/* Subtle circle pattern on the back (matches the reference) */
.cb-pattern{position:absolute;inset:0;
  background-image:radial-gradient(circle, rgba(255,255,255,.07) 1.1px, transparent 1.5px);
  background-size:9px 9px;pointer-events:none;}

.cb-magstripe{height:32px;background:#09090E;margin-top:14px;width:100%;flex-shrink:0;position:relative;z-index:1;}
.cb-body{padding:10px 18px 6px;flex:1;display:flex;flex-direction:column;position:relative;z-index:1;}
.cb-block{margin-bottom:4px;}
.cb-heading{font-size:11px;font-weight:800;letter-spacing:1.2px;margin-bottom:3px;text-transform:uppercase;}
.cb-text{font-size:8px;color:rgba(255,255,255,.92);line-height:1.4;}
.cb-text-strong{font-size:8.5px;color:#fff;font-weight:700;line-height:1.3;}
.cb-divider{border-bottom:1px solid;opacity:.45;margin:4px 0 6px;}
.cb-divider-spark{position:relative;border-bottom:1px solid rgba(0,215,242,.25);margin:4px 18px 6px;flex-shrink:0;z-index:1;}
.cb-divider-spark::after{content:'';position:absolute;top:50%;left:50%;width:5px;height:5px;background:#00D7F2;border-radius:50%;transform:translate(-50%,-50%);box-shadow:0 0 6px rgba(0,215,242,.85);}
.cb-return-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:2px;}
.cb-col-divider{border-left:1px solid rgba(255,255,255,.25);padding-left:8px;}

.cb-footer{display:flex;align-items:flex-end;justify-content:space-between;padding:0 18px 8px;position:relative;z-index:1;gap:12px;}
.cb-accepted{display:flex;align-items:center;gap:6px;flex:1;min-width:0;}
.cb-accepted-icon{width:28px;height:28px;object-fit:contain;flex-shrink:0;}
.cb-accepted-block{display:flex;flex-direction:column;line-height:1.05;min-width:0;}
.cb-accepted-lbl{font-size:7.5px;color:rgba(255,255,255,.85);font-weight:500;font-style:italic;letter-spacing:.2px;margin-bottom:1px;}
.cb-accepted-name{font-size:14px;font-weight:900;letter-spacing:.4px;line-height:1;text-transform:uppercase;}
.cb-accepted-sub{font-size:8px;font-weight:700;letter-spacing:2.5px;line-height:1.1;text-transform:uppercase;margin-top:1px;}
.cb-powered-stack{display:flex;flex-direction:column;align-items:flex-end;gap:2px;}
.cb-powered-row{display:flex;align-items:center;gap:5px;}
.cb-powered-text{font-size:8px;color:rgba(255,255,255,.78);font-weight:500;font-style:italic;}
.cb-simi-logo{height:20px;width:auto;object-fit:contain;}
.cb-product{font-size:6.5px;color:rgba(255,255,255,.55);letter-spacing:.6px;font-weight:600;text-align:right;}

/* ===== Manage Card Fees modal ===== */
.cf-overlay{position:fixed;inset:0;background:rgba(5,10,20,.78);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1050;padding:20px;}
.cf-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;box-shadow:0 24px 60px rgba(0,0,0,.5);width:100%;max-width:1100px;max-height:90vh;display:flex;flex-direction:column;padding:20px;overflow:hidden;}
.cf-title{display:flex;align-items:center;justify-content:space-between;color:#E8EDF4;font-size:16px;font-weight:700;padding-bottom:12px;border-bottom:1px solid #1A3A6A;margin-bottom:14px;}
.cf-close{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;width:30px;height:30px;border-radius:8px;font-size:14px;cursor:pointer;line-height:1;}
.cf-close:hover{color:#E8EDF4;border-color:#4FC3F7;}
.cf-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin-bottom:14px;}
.cf-stat{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;text-align:center;}
.cf-stat-num{font-size:20px;font-weight:800;color:#E8EDF4;line-height:1.1;}
.cf-stat-lbl{font-size:11px;color:#8BA4C4;margin-top:4px;font-weight:600;letter-spacing:.3px;}
.cf-lbl{display:block;color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;margin-bottom:4px;}
.cf-input{background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:7px 10px;color:#E8EDF4;font-size:13px;outline:none;min-width:140px;}
.cf-input:focus{border-color:#2E86DE;}
.cf-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:7px 14px;font-size:12px;}
.cf-btn-warn{background:linear-gradient(135deg,#F59E0B,#D97706);color:#fff;border:none;font-weight:700;border-radius:8px;padding:7px 14px;font-size:12px;}
.cf-btn-warn:disabled{opacity:.5;cursor:not-allowed;}
.cf-table-wrap{flex:1;overflow:auto;border:1px solid #1A3A6A;border-radius:10px;background:#0A1628;}
.cf-table{width:100%;border-collapse:collapse;font-size:13px;color:#E8EDF4;}
.cf-table thead th{background:#0F2347;color:#8BA4C4;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;padding:10px 12px;text-align:left;border-bottom:1px solid #1A3A6A;position:sticky;top:0;z-index:1;}
.cf-table tbody td{padding:9px 12px;border-bottom:1px solid rgba(26,58,106,.4);}
.cf-table tbody tr:hover{background:rgba(46,134,222,.08);}
.cf-pill{display:inline-block;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.3px;}
.cf-pill-ok{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.4);}
.cf-pill-warn{background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.4);}
.cf-pill-bad{background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.4);}
</style>
